"use client";
import { Avatar } from "@base-ui/react/avatar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postCommentReplyAction } from "@/actions/content.action";
import { uploadFile } from "@/actions/file.action";
import TopicReplyEditor from "@/app/(main)/forum/_components/topic-reply-editor";

type CurrentUser = { name: string; surname: string; imageUrl?: string };

interface Props {
  commentId: string;
  contentId: string;
  currentUser?: CurrentUser;
  onClose: () => void;
}

const CommentReplyForm = ({
  contentId,
  commentId,
  currentUser: user,
  onClose,
}: Props) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: async ({ html, images }: { html: string; images: File[] }) => {
      let imageUrls: string[] = [];
      if (images.length > 0) {
        const results = await Promise.all(
          images.map((file) => uploadFile(file, "content/comments")),
        );
        imageUrls = results.flatMap((r) => (r.isSuccess ? [r.result.filePath] : []));
      }
      return postCommentReplyAction(contentId, commentId, html, imageUrls);
    },
    onSuccess: (res) => {
      if (res.isSuccess) {
        queryClient.invalidateQueries({ queryKey: ["replies", commentId] });
        onClose();
      }
    },
  });

  const displayName = user ? `${user.name} ${user.surname}` : "";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("");

  return (
    <div className="flex flex-row gap-3 mt-3 items-start">
      <Avatar.Root className="inline-flex size-8 items-center justify-center overflow-hidden rounded-full bg-gray-100 align-middle text-xs font-medium text-gray-900 select-none shrink-0">
        {user?.imageUrl && (
          <Avatar.Image
            className="size-full object-cover"
            src={user.imageUrl}
          />
        )}
        <Avatar.Fallback className="flex size-full items-center justify-center text-xs">
          {initials}
        </Avatar.Fallback>
      </Avatar.Root>
      <div className="flex-1">
        <TopicReplyEditor
          autoFocus={true}
          isPending={isPending}
          onCancel={onClose}
          onSubmit={(html, images) => mutate({ html, images })}
          placeholder="Yanıtını yaz..."
        />
      </div>
    </div>
  );
};

export default CommentReplyForm;
